﻿<script setup lang="ts">
    import * as signalR from '@microsoft/signalr';
    import { BButton, BModal, BNotification, BProgress } from 'buefy';
    import { computed, onBeforeUnmount, ref, watch } from 'vue';
    import CodeEditSaveProgressStateVisualizer from '@/shared/components/CodeEditSaveProgressStateVisualizer.vue';
    import useShow from '@/shared/composables/useShow';
    import { getRelease } from '@/shared/helpers/api.ts';
    import type { ReleaseState, SubReleaseState } from '@/shared/helpers/api/types/entities.ts';
    import { getReleaseLink } from '@/shared/helpers/routing.ts';
    import { http } from '@/shared/httpWrapper';
    import { Interval } from '@/shared/intervalState.ts';

    type Props = {
        saveProcessStarted?: boolean;
        releaseId?: number | null;
        externalError?: Error | null;
        closeEnabled?: boolean;
        isReleaseEmpty?: boolean;
    };

    const props = withDefaults(defineProps<Props>(), {
        saveProcessStarted: false,
        releaseId: null,
        externalError: null,
        closeEnabled: true,
        isReleaseEmpty: false
    });

    const emit = defineEmits<{
        success: [];
        close: [];
    }>();

    const { showError } = useShow();

    const dialogClosed = ref(false);
    const releaseState = ref<ReleaseState | null>(null);
    const isVisible = ref(false);
    const externalErrorMessage = ref('');
    const subReleases = ref<SubReleaseState[]>([]);
    const isReleaseChain = ref(false);
    const connection = ref<signalR.HubConnection | null>(null);
    const lastSignalRNotification = ref<number>(Date.now());
    const interval = new Interval();

    const isDone = computed(() => {
        if (isReleaseChain.value) {
            return releaseState.value === 'Published';
        }

        return ['Committed', 'Published', 'Publishing'].includes(releaseState.value ?? '');
    });

    const isFailure = computed(() => {
        // An AutoForward job is not supposed to reach the Open state
        // unless a transition fails.
        if (isReleaseChain.value) {
            return subReleases.value.some(r => r.state === 'Open');
        }

        return releaseState.value === 'Open';
    });

    const isDiscarded = computed(() => {
        if (isReleaseChain.value) {
            return subReleases.value.some(r => r.state === 'Discarded');
        }

        return releaseState.value === 'Discarded';
    });

    watch(() => props.saveProcessStarted, (value) => {
        // New process started
        if (value) {
            releaseState.value = null;
            subReleases.value = [];
            externalErrorMessage.value = '';
            dialogClosed.value = false;
        }

        setIsVisible();
    });

    watch(dialogClosed, () => {
        setIsVisible();
    });

    watch(() => props.releaseId, async (value) => {
        if (value) {
            setIsVisible();
            // Have to load release now, as events might already have happened
            await loadRelease();
        }
    });

    watch(isDone, (value) => {
        if (value) {
            signalSuccess();
        }
    });

    watch(() => props.externalError, (value) => {
        if (value) {
            showError(value, (extractedMessage: string) => {
                externalErrorMessage.value = extractedMessage;
            });
        }
    });

    watch(isVisible, async (value) => {
        if (value) {
            await setupSignalRConnection();
            setupInterval();
            if (props.releaseId) {
                await loadRelease();
            }
            return;
        }

        interval.clearInterval();
        await closeSignalRConnection();
    });

    onBeforeUnmount(() => {
        interval.clearInterval();
        void closeSignalRConnection();
    });

    async function closeSignalRConnection() {
        await connection.value?.stop();
        connection.value = null;
    }

    async function setupSignalRConnection() {
        connection.value = new signalR.HubConnectionBuilder()
            .withUrl(window.config!.signalRUrlMyReleases, {
                accessTokenFactory: async () => await window.authService.acquireToken()
            })
            .configureLogging(signalR.LogLevel.Error)
            .build();

        try {
            connection.value.on('change', releaseId => onSignalRChange(releaseId));
            await connection.value.start();
        } catch {
            return;
        }
    }

    function setupInterval() {
        interval.setInterval(async () => {
            if (props.releaseId !== null && (Date.now() - lastSignalRNotification.value) > 4000) {
                await loadRelease();
            }
        }, 5000);
    }

    function setIsVisible() {
        if (props.saveProcessStarted || isVisible.value) {
            isVisible.value = !dialogClosed.value;
        }
    }

    async function onSignalRChange(releaseId: string | number) {
        const id = Number.parseInt(String(releaseId), 10);

        if (Number.isNaN(id) || id !== props.releaseId) {
            return; // another release changed
        }

        lastSignalRNotification.value = Date.now();
        await loadRelease();
    }

    async function loadRelease() {
        if (!props.releaseId) {
            return;
        }

        const release = await getRelease(http, props.releaseId);
        releaseState.value = release.state;
        isReleaseChain.value = release.specification.specificationType === 'ReleaseChain';
        subReleases.value = release.specification.specificationType === 'ReleaseChain'
            ? release.specification.subReleases ?? []
            : [];
    }

    function signalSuccess() {
        setTimeout(() => {
            closeDialog();
            emit('success');
        }, 500);
    }

    function closeModal() {
        closeDialog();
        emit('close');
    }

    function closeDialog() {
        dialogClosed.value = true;
    }

    function getReleaseRoute(value: number | null) {
        if (value === null) {
            throw new Error('Release id is required');
        }

        return getReleaseLink(value);
    }
</script>

<template>
    <b-modal
        :model-value="isVisible"
        :can-cancel="false"
        has-modal-card>
        <div
            class="modal-card"
            style="height: auto; width: auto;">
            <header
                class="modal-card-head">
                <div class="modal-card-title">
                    Processing changes, please wait..
                </div>
            </header>
            <section class="modal-card-body">
                <b-progress
                    v-show="!isFailure && !props.isReleaseEmpty"
                    size="is-small" />
                <div
                    v-if="isReleaseChain"
                    class="block">
                    <p class="block">
                        This is a release chain publishing changes to multiple libraries.
                    </p>
                    <code-edit-save-progress-state-visualizer
                        v-for="subRelease in subReleases"
                        :key="subRelease.releaseId"
                        :library-name="subRelease.library"
                        :state="subRelease.state" />
                </div>
                <code-edit-save-progress-state-visualizer
                    v-else
                    :state="releaseState" />

                <p
                    v-show="!isFailure && !props.isReleaseEmpty"
                    class="block">
                    <strong>This window will close</strong> once your changes have been <strong style="color:#fd5d00;">verified</strong>
                    and <strong style="color:green;">committed</strong>.
                    This may depend on approval from external systems.
                </p>

                <b-notification
                    v-if="isFailure"
                    type="is-danger"
                    has-icon
                    :closable="false"
                    role="alert">
                    Saving your changes seem to have failed!<br>
                    Please
                    <router-link
                        :to="getReleaseRoute(props.releaseId)">
                        <span>open the release page</span>
                    </router-link>
                    to examine what went wrong.
                </b-notification>

                <b-notification
                    v-show="props.externalError"
                    type="is-danger"
                    has-icon
                    :closable="false"
                    role="alert">
                    Unable to save changes!<br>
                    {{ externalErrorMessage }}
                </b-notification>

                <b-notification
                    v-show="props.isReleaseEmpty"
                    type="is-info"
                    has-icon
                    :closable="false"
                    role="alert">
                    No changes to commit!
                </b-notification>
            </section>
            <footer class="modal-card-foot is-justify-content-end">
                <b-button
                    v-if="props.releaseId"
                    tag="router-link"
                    icon-left="link-variant"
                    label="Go to Release"
                    :to="getReleaseRoute(props.releaseId)" />
                <b-button
                    v-if="props.closeEnabled || props.externalError || isDiscarded"
                    icon-left="close-circle"
                    label="Close"
                    @click="closeModal" />
            </footer>
        </div>
    </b-modal>
</template>
